<script setup lang="ts">
import Button from 'primevue/button';
import { Job, JobCollection, STATUS } from '@/models/Job';
import DataView from 'primevue/dataview';
import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';
import ProgressBar from 'primevue/progressbar';
import Tag from 'primevue/tag';
import { JOB_TYP } from '@/models/Job';
import { buildUrl } from '@/helpers/api';
import { useI18n } from 'vue-i18n';
import { filenameToIconCls } from '@/helpers/tools';

const t = useI18n().t;
const props = withDefaults(
    defineProps<{
        typ: string;
        jobCollection?: JobCollection;
    }>(),
    {
        jobCollection: () => new JobCollection(),
    },
);

let timer: number | null = null;

onMounted(async () => {
    props.jobCollection.setQueryParams({
        filter: [
            {
                id: 'typ',
                property: 'typ',
                value: props.typ,
            },
            {
                id: 'status',
                property: 'status',
                value: STATUS.QUEUED,
                operator: '!=',
            },
        ],
    });
    await props.jobCollection.query();
    timer = setInterval(async () => {
        await props.jobCollection.query();
    }, 10000);
});

onBeforeUnmount(() => {
    if (timer) {
        clearInterval(timer);
    }
});

function downloadFile(job: Job) {
    const link = document.createElement('a');
    link.href = buildUrl('/Auswertung/download', { job_id: job.id });
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    link.remove();
    props.jobCollection.query();
}

function getFormattedJobFilter(filter: object) {
    const res = [];
    for (const key in filter) {
        let val = '';

        if ((filter as Record<string, any>)[key] === true) {
            val = t('ja');
        } else if ((filter as Record<string, any>)[key] === false) {
            val = t('nein');
        } else if (!(filter as Record<string, any>)[key]) {
            val = '-';
        } else {
            val = (filter as Record<string, any>)[key];
        }
        res.push(`${t(key)}: <b>${val}</b>`);
    }
    return res;
}
</script>

<template>
    <DataView :value="jobCollection.getModels()" dataKey="id">
        <template #list="slotProps">
            <div class="flex flex-col flex-wrap">
                <div class="w-full" v-for="(item, index) in slotProps.items" :key="index">
                    <div
                        class="flex flex-col sm:flex-row sm:items-center p-6 gap-4 align-items-center"
                        :class="{ 'border-top-1 border-black-alpha-10': index !== 0 }"
                    >
                        <!-- Icon: fest, nicht schrumpfend -->
                        <div class="flex-shrink-0" style="width: 3rem">
                            <i :class="filenameToIconCls(item.job_daten.filename) + ' fa-2xl'"></i>
                        </div>

                        <!-- Filename/Kanton: nimmt so viel Platz wie möglich -->
                        <div class="flex-1 job-info-section">
                            <div v-if="typ === JOB_TYP.AUSWERTUNG_GEBAEUDE || typ === JOB_TYP.AUSWERTUNG_PRODUKTION">
                                <div class="text-sm text-ellipsis">{{ $t(item.job_daten.typ) }}</div>
                                <div class="text-lg mt-2 text-ellipsis">
                                    {{ item.job_daten.kanton }}, {{ item.job_daten.jahr }}
                                </div>
                            </div>
                            <div v-else>
                                <div class="text-sm text-ellipsis">{{ item.job_daten.filename }}</div>
                                <div class="text-lg mt-2 text-ellipsis">
                                    {{ item.job_daten.kanton }}, {{ item.job_daten.jahr }}
                                </div>
                            </div>
                        </div>

                        <!-- Filter: kann schrumpfen und umbrechen -->
                        <div v-if="item.job_daten.filter" class="text-sm flex-shrink-2 job-filter-section">
                            <div v-for="(filter, idx) in getFormattedJobFilter(item.job_daten.filter)" :key="idx">
                                <div class="filter-item" v-html="filter"></div>
                            </div>
                        </div>

                        <!-- Rechte Seite: Datum und Status -->
                        <div
                            class="flex flex-col sm:flex-row justify-content-end sm:items-center gap-4 sm:gap-6 flex-shrink-0 sm:ml-auto"
                        >
                            <div class="text-sm flex-shrink-0 job-date-section">
                                <span class="text-sm">{{ $t('erstellt_am') }}</span>
                                <div class="text-sm">
                                    {{ item.created.format('DD.MM.YYYY HH:mm') }}
                                </div>
                            </div>
                            <div class="flex-shrink-0 job-status-section">
                                <div>
                                    <span class="text-sm">
                                        <Tag
                                            v-if="item.status === STATUS.CANCELED"
                                            severity="secondary"
                                            :value="$t('abgebrochen')"
                                        ></Tag>
                                        <template v-else-if="item.status === STATUS.SUCCESS">
                                            <Button
                                                v-if="
                                                    typ === JOB_TYP.AUSWERTUNG_GEBAEUDE ||
                                                    typ === JOB_TYP.AUSWERTUNG_PRODUKTION
                                                "
                                                type="button"
                                                :label="$t('download')"
                                                icon="fa-solid fa-download"
                                                severity="success"
                                                @click="downloadFile(item)"
                                            ></Button>
                                            <Tag v-else severity="success" :value="$t('importiert')"></Tag>
                                        </template>
                                        <Tag
                                            v-else-if="item.status === STATUS.FAILED"
                                            severity="danger"
                                            :value="$t('fehler')"
                                            v-tooltip.left="item.job_daten.error"
                                        ></Tag>
                                        <Tag v-else>
                                            <span
                                                v-if="
                                                    typ === JOB_TYP.AUSWERTUNG_GEBAEUDE ||
                                                    typ === JOB_TYP.AUSWERTUNG_PRODUKTION
                                                "
                                                >{{ $t('auswertung_laeuft') }}</span
                                            >
                                            <span v-else>{{ $t('import_laeuft') }}</span>
                                            <i class="fa-solid fa-circle-notch fa-spin"></i>
                                        </Tag>
                                    </span>
                                    <div
                                        v-if="item.status === STATUS.RUNNING || item.status === STATUS.NEW"
                                        class="text-lg mt-2"
                                    >
                                        <ProgressBar
                                            v-if="item.progress > 0"
                                            :value="item.progress"
                                            :showValue="false"
                                            style="height: 6px"
                                        ></ProgressBar>
                                        <ProgressBar v-else mode="indeterminate" style="height: 6px"></ProgressBar>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </DataView>
</template>

<style lang="css" scoped>
.filter-item {
    text-overflow: ellipsis;
    overflow: hidden;
    text-wrap-mode: nowrap;
}

.job-info-section {
    min-width: 8rem;
}

.text-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.job-filter-section {
    min-width: 0;
    max-width: 20rem;
}

.job-date-section {
    min-width: 7rem;
}

.job-status-section {
    min-width: 9rem;
    display: flex;
    justify-content: center;
    align-items: center;
}

/* Mobile: Alle Elemente volle Breite */
@media (max-width: 640px) {
    .job-info-section,
    .job-filter-section,
    .job-date-section,
    .job-status-section {
        max-width: 100%;
        width: 100%;
    }
}
</style>
